import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import {
  Upload, FileText, Trash2,
  Sparkles, X, Check,
  FolderOpen, Save, Zap, Mail,
  MapPin, ChevronDown, ShieldCheck,
  Link, Loader2, Shield, RefreshCw, Cpu, Lock, Briefcase, Rocket, Scissors
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useFeatures } from "../lib/features";
import { engineLabel, ENGINES, ENGINE_KEYS_FULL, MODEL_LOCK_MESSAGE } from "../lib/models";
import { EmploymentSelect } from "../components/FlowSelectors";
import { useLocation } from "react-router-dom";
import AnalysisStep from "./AnalysisStep";
import ResultsStep from "./ResultsStep";
import { WaitingUX } from "../components/WaitingUX";
import { useJobProgress } from "../lib/useJobProgress";

const VISA_OPTIONS = [
  { value: "", label: "Select visa status..." },
  { value: "H1B", label: "H1B" },
  { value: "OPT", label: "OPT" },
  { value: "OPT STEM", label: "OPT STEM" },
  { value: "CPT", label: "CPT" },
  { value: "Green Card", label: "Green Card" },
  { value: "Green Card EAD", label: "Green Card EAD" },
  { value: "L1", label: "L1" },
  { value: "US Citizen", label: "US Citizen" },
  { value: "TN Visa", label: "TN Visa" },
];

const PREFS_KEY = "resumeai_defaults";
const EMPLOYMENT_LABELS = {
  "contract-c2c": "Contract C2C",
  "full-time": "Full-Time",
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function savePrefs(prefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch {}
}

function detectLocalOnly(jdText) {
  if (!jdText) return false;
  const lower = jdText.toLowerCase();
  const patterns = [
    "local candidates only", "local candidates preferred",
    "must be local", "only local candidates", "local to",
    "candidates must be local", "no relocation", "no remote",
    "onsite only", "on-site only", "in-office only",
    "local applicants only", "must reside in", "must be based in",
  ];
  return patterns.some(p => lower.includes(p));
}

function extractLocationFromJD(jdText) {
  if (!jdText) return "";
  const patterns = [
    /(?:location|located|based|office|position)\s*(?:in|:)\s*([A-Z][a-zA-Z\s]+,\s*[A-Z]{2})/i,
    /(?:in|at)\s+([A-Z][a-zA-Z\s]+,\s*[A-Z]{2})(?:\s*[.\-]|\s+(?:local|area|metro|region))/i,
    /([A-Z][a-zA-Z]+,\s*[A-Z]{2})\s*(?:area|metro|region|location)/i,
    /(?:local to|onsite in|on-site in)\s+([A-Z][a-zA-Z\s,]+?)(?:\.|,|\n|$)/i,
  ];
  for (const re of patterns) {
    const m = jdText.match(re);
    if (m) return m[1].trim();
  }
  return "";
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


/* ── Reusable UI atoms (shared visual language with Analysis/Results) ── */
function Toggle({ on, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`relative shrink-0 rounded-full transition-colors ${on ? "bg-primary" : "bg-muted-foreground/30"}`}
      style={{ height: 22, width: 40 }}>
      <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform"
        style={{ transform: on ? "translateX(18px)" : "translateX(0)" }} />
    </button>
  );
}

function ToggleRow({ icon: Icon, label, desc, on, onClick, testid, badge }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5" data-testid={testid}>
      <div className="flex items-center gap-2.5 min-w-0">
        {Icon && <Icon size={15} className="text-muted-foreground shrink-0" />}
        <div className="min-w-0">
          <span className="text-sm font-medium text-foreground flex items-center gap-2">{label}{badge}</span>
          {desc && <span className="block text-[0.72rem] text-muted-foreground">{desc}</span>}
        </div>
      </div>
      <Toggle on={on} onClick={onClick} testid={testid ? `${testid}-switch` : undefined} />
    </div>
  );
}

function ChoiceChip({ active, onClick, icon: Icon, label, badge, badgeClass, testid, locked }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-disabled={locked || undefined}
      title={locked ? MODEL_LOCK_MESSAGE : undefined}
      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all active:scale-[0.98] ${locked ? "opacity-55 border-border bg-background text-muted-foreground cursor-not-allowed" : active ? "border-primary bg-primary/5 ring-1 ring-primary/20 text-foreground" : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground"}`}>
      {locked ? <Lock size={13} /> : Icon && <Icon size={14} />}<span>{label}</span>
      {badge && <span className={`text-[0.58rem] font-mono uppercase px-1.5 py-0.5 rounded ${badgeClass || "bg-foreground/10 text-foreground"}`}>{badge}</span>}
    </button>
  );
}


/* ── Upload Section ── */
function UploadSection({ onResult }) {
  const [file, setFile] = useState(null);
  const [jobDescription, setJobDescription] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(0);
  const [jobStep, setJobStep] = useState("");
  const optimizeProgress = useJobProgress({ active: processing, phase: "optimize", step: jobStep });
  const [savedResumes, setSavedResumes] = useState([]);
  const [selectedSavedId, setSelectedSavedId] = useState("");
  const [showSaveOption, setShowSaveOption] = useState(false);
  const [saveDisplayName, setSaveDisplayName] = useState("");
  const [loadingResumes, setLoadingResumes] = useState(true);
  const [resumeSource, setResumeSource] = useState("upload");
  const prefs = useRef(loadPrefs());
  const [formatMode, setFormatMode] = useState(prefs.current?.formatMode ?? "clone_fill");
  const [aiModel, setAiModel] = useState(prefs.current?.aiModel ?? "glm-5.2");
  const { user } = useAuth();
  const { features } = useFeatures();
  const isModelLocked = (m) => features?.[ENGINES[m]?.lockKey] === true && user?.role !== "admin";
  const selectModel = (m) => {
    if (isModelLocked(m)) { toast.error(MODEL_LOCK_MESSAGE); return; }
    setAiModel(m); savePrefs({ aiModel: m });
  };
  // If the currently selected engine gets locked by an admin, fall back to an available one.
  useEffect(() => {
    if (isModelLocked(aiModel)) {
      const alt = Object.keys(ENGINES).find((m) => m !== aiModel && !isModelLocked(m));
      if (alt) { setAiModel(alt); savePrefs({ aiModel: alt }); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, aiModel]);
  const [deepOptimize, setDeepOptimize] = useState(prefs.current?.deepOptimize ?? false);
  const [hygiene, setHygiene] = useState(prefs.current?.hygiene ?? true);
  const [prune, setPrune] = useState(prefs.current?.prune ?? false);
  const [personalInfo, setPersonalInfo] = useState({ full_name: "", phone: "", email: "", location: "" });
  const [showPersonalInfo, setShowPersonalInfo] = useState(false);
  const [generateEmail, setGenerateEmail] = useState(prefs.current?.generateEmail ?? true);
  const [generateCoverLetter, setGenerateCoverLetter] = useState(prefs.current?.generateCoverLetter ?? false);
  const [showCoverLetterPopup, setShowCoverLetterPopup] = useState(false);
  const [clIsThirdParty, setClIsThirdParty] = useState(false);
  const [employmentType, setEmploymentType] = useState(prefs.current?.employmentType ?? "contract-c2c");
  const [includeVisa, setIncludeVisa] = useState(prefs.current?.includeVisa ?? false);
  const [visaStatus, setVisaStatus] = useState(prefs.current?.visaStatus ?? "");
  const [matchLocation, setMatchLocation] = useState(prefs.current?.matchLocation ?? false);
  const [jobLocation, setJobLocation] = useState(prefs.current?.jobLocation ?? "");
  const [candidateLocation, setCandidateLocation] = useState(prefs.current?.candidateLocation ?? "");
  const [localDetected, setLocalDetected] = useState(false);
  const [jdInputMode, setJdInputMode] = useState("text"); // "text" or "link"
  const [jobUrl, setJobUrl] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [extractedPlatform, setExtractedPlatform] = useState("");
  const [recruiterEmail, setRecruiterEmail] = useState("");
  const [recruiterName, setRecruiterName] = useState("");
  const [defaultsSaved, setDefaultsSaved] = useState(false);
  const [emailLength, setEmailLength] = useState(prefs.current?.emailLength ?? "short");
  const [doNotDiscussLocation, setDoNotDiscussLocation] = useState(prefs.current?.doNotDiscussLocation ?? false);
  const [willingToRelocate, setWillingToRelocate] = useState(prefs.current?.willingToRelocate ?? false);

  // ── Phased flow: Scan → Analysis/Consent → Optimize → Applied ──
  const [scanning, setScanning] = useState(false);
  const [scanData, setScanData] = useState(null);
  const [selectedActions, setSelectedActions] = useState([]);
  const [skillPlacements, setSkillPlacements] = useState({});
  const [showAppliedPopup, setShowAppliedPopup] = useState(false);
  const [pendingResult, setPendingResult] = useState(null);
  const [savingApplication, setSavingApplication] = useState(false);
  const [resumeRename, setResumeRename] = useState("");
  const [scanId, setScanId] = useState("");
  const location = useLocation();
  const incomingNav = location.state;

  // All toggles are fully independent — LLM interprets JD + toggles
  const handleMatchLocationToggle = () => setMatchLocation(!matchLocation);
  const handleDoNotDiscussToggle = () => setDoNotDiscussLocation(!doNotDiscussLocation);
  const handleRelocateToggle = () => setWillingToRelocate(!willingToRelocate);

  // Sync candidateLocation to profile on blur
  const syncLocationToProfile = async (loc) => {
    try { await axios.put(`${BACKEND_URL}/api/auth/profile`, { current_location: loc }); } catch {}
  };

  // Load location from profile on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/auth/profile`);
        if (data.current_location && !candidateLocation) {
          setCandidateLocation(data.current_location);
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Auto-save current state as preferences whenever key values change
    savePrefs({ generateEmail, generateCoverLetter, includeVisa, visaStatus, matchLocation, jobLocation, candidateLocation, formatMode, deepOptimize, hygiene, prune, emailLength, doNotDiscussLocation, willingToRelocate, aiModel, employmentType });
  }, [generateEmail, generateCoverLetter, includeVisa, visaStatus, matchLocation, jobLocation, candidateLocation, formatMode, deepOptimize, hygiene, prune, emailLength, doNotDiscussLocation, willingToRelocate, employmentType]);

  useEffect(() => {
    const isLocal = detectLocalOnly(jobDescription);
    setLocalDetected(isLocal);
    if (isLocal && !matchLocation) setMatchLocation(true);
    if (isLocal && !jobLocation) {
      const loc = extractLocationFromJD(jobDescription);
      if (loc) setJobLocation(loc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDescription]);

  useEffect(() => { fetchSavedResumes(); }, []);

  // Unified flow: if the user came from the Match Score page, reuse that analysis
  // (skip a second scan) and drop them straight onto the consent/Analysis screen.
  useEffect(() => {
    if (incomingNav?.fromMatchScore && incomingNav.scanResult) {
      setScanData(incomingNav.scanResult);
      setScanId(incomingNav.scanId || "");
      setSelectedActions((incomingNav.scanResult.action_plan || []).filter(a => a.default_checked).map(a => a.id));
      setSkillPlacements({});
      if (incomingNav.jobDescription) setJobDescription(incomingNav.jobDescription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSavedResumes = async () => {
    try {
      const res = await axios.get(`${API}/resumes`);
      setSavedResumes(res.data);
      if (res.data.length > 0) {
        // Auto-select the default resume first, otherwise the most recent
        const defaultResume = res.data.find(r => r.is_default);
        setResumeSource("saved");
        setSelectedSavedId(incomingNav?.resumeId || (defaultResume ? defaultResume.id : res.data[0].id));
      }
    } catch { /* silent */ } finally { setLoadingResumes(false); }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const f = acceptedFiles[0];
    if (f) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (ext === "docx" || ext === "doc") {
        setFile(f); setSelectedSavedId(""); setResumeSource("upload");
      } else { toast.error("Please upload resume in DOC/DOCX format only."); }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/msword": [".doc"],
    },
    maxFiles: 1,
  });

  const handleSaveResume = async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append("resume", file);
    formData.append("display_name", saveDisplayName || file.name.replace(/\.[^/.]+$/, ""));
    try {
      const res = await axios.post(`${API}/resumes/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume saved to library!");
      setShowSaveOption(false); setSaveDisplayName("");
      await fetchSavedResumes();
      setSelectedSavedId(res.data.id); setResumeSource("saved"); setFile(null);
    } catch { toast.error("Failed to save resume"); }
  };

  const handleDeleteSaved = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/resumes/${id}`);
      setSavedResumes((prev) => prev.filter((r) => r.id !== id));
      if (selectedSavedId === id) { setSelectedSavedId(""); setResumeSource("upload"); }
      toast.success("Resume removed from library");
    } catch { toast.error("Failed to delete resume"); }
  };

  const handleExtractJD = async () => {
    if (!jobUrl.trim()) { toast.error("Please enter a job URL"); return; }
    setExtracting(true);
    try {
      const res = await axios.post(`${API}/jd/extract`, { url: jobUrl.trim() }, { timeout: 25000 });
      const data = res.data;
      if (data.error) {
        toast.error(data.error, { duration: 6000 });
        setJdInputMode("text");
        return;
      }
      if (data.job_description) setJobDescription(data.job_description);
      if (data.job_title) setJobTitle(data.job_title);
      // Only auto-fill company name if the scraper is confident it's correct
      if (data.company_name && data.company_name_confidence === "high") {
        setCompanyName(data.company_name);
      } else if (data.company_name && data.company_name_confidence !== "high") {
        // Uncertain — leave blank so user enters manually
        toast.info("Company name could not be confirmed. Please enter it manually.");
      }
      if (data.location) setJobLocation(data.location);
      if (data.platform) setExtractedPlatform(data.platform);
      if (data.recruiter_email) setRecruiterEmail(data.recruiter_email);
      if (data.job_description) {
        toast.success(`Extracted from ${data.platform || "job page"}!`);
        setJdInputMode("text");
      } else {
        toast.error("Could not extract job description. Please paste it manually.", { duration: 5000 });
        setJdInputMode("text");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to extract. Try pasting the JD manually.";
      toast.error(msg, { duration: 6000 });
      setJdInputMode("text");
    } finally { setExtracting(false); }
  };



  const handleOptimize = async () => {
    const hasResumeNow = resumeSource === "saved" ? !!selectedSavedId : !!file;
    if (!hasResumeNow || !jobDescription.trim()) {
      toast.error("Please select a resume and enter a job description"); return;
    }
    await runScan();
  };

  // ── Phase 2: SCAN (analysis on the selected engine) — async + polling ──
  const runScan = async () => {
    if (isModelLocked(aiModel)) { toast.error(MODEL_LOCK_MESSAGE); return; }
    setScanning(true);
    const formData = new FormData();
    if (resumeSource === "saved") formData.append("saved_resume_id", selectedSavedId);
    else formData.append("resume", file);
    formData.append("job_description", jobDescription);
    formData.append("ai_model", aiModel);
    formData.append("employment_type", employmentType);
    try {
      const res = await axios.post(`${API}/scan`, formData, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      const sid = res.data.scan_id;
      if (!sid) throw new Error("Scan could not be started");
      setScanId(sid);
      // Poll until the analysis completes (GLM-5.2 can take up to ~60s).
      const started = Date.now();
      let data = null;
      while (Date.now() - started < 240000) {
        await new Promise(r => setTimeout(r, 1500));
        const st = await axios.get(`${API}/scan/status/${sid}`, { timeout: 30000 });
        if (st.data.status === "completed") { data = st.data.result; break; }
        if (st.data.status === "failed") throw new Error(st.data.error || "Scan failed");
      }
      if (!data) throw new Error("Scan timed out. Please try again.");
      setScanData(data);
      // Default consent comes from the backend's action plan (safe actions pre-ticked,
      // aspirational/no-evidence skills left unticked until the user opts in).
      setSelectedActions((data.action_plan || []).filter(a => a.default_checked).map(a => a.id));
      setSkillPlacements({});
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Scan failed. Please try again.";
      toast.error(msg, { duration: 7000 });
    } finally { setScanning(false); }
  };

  const toggleAction = (id) => {
    setSelectedActions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllActions = (selectAll) => {
    setSelectedActions(selectAll ? (scanData?.action_plan || []).map(a => a.id) : []);
  };

  const handleBackToInput = () => { setScanData(null); setSelectedActions([]); setSkillPlacements({}); };

  // ── Phase 4: proceed to optimize with consent ──
  const handleProceedToOptimize = async () => {
    if (generateCoverLetter) { setShowCoverLetterPopup(true); return; }
    await startOptimization();
  };

  // ── Phase 6: applied tracking ──
  const handleApplied = async (didApply) => {
    if (didApply) {
      if (!companyName.trim()) { toast.error("Please enter the company name"); return; }
      setSavingApplication(true);
      try {
        const today = new Date().toISOString().slice(0, 10);
        await axios.post(`${API}/applications`, {
          company: companyName || "Unknown Company",
          job_title: jobTitle || "Untitled Role",
          status: "applied",
          url: jobUrl || "",
          platform: extractedPlatform || "",
          job_description: jobDescription || "",
          location: jobLocation || "",
          recruiter_email: recruiterEmail || "",
          optimization_id: pendingResult?.id || "",
          applied_date: today,
          date_saved: today,
        });
        toast.success("Added to your Job Tracker!");
      } catch {
        toast.error("Could not add to tracker, but your resume is ready.");
      } finally { setSavingApplication(false); }
    }
    setShowAppliedPopup(false);
    const result = pendingResult;
    setPendingResult(null);
    setScanData(null);
    setSelectedActions([]);
    if (result) onResult(result);
  };

  const handleCoverLetterPopupSubmit = async () => {
    if (!companyName.trim()) {
      toast.error("Company name is required for cover letter"); return;
    }
    setShowCoverLetterPopup(false);
    await startOptimization();
  };

  const startOptimization = async () => {
    setProcessing(true); setStep(0); setJobStep("optimizing");
    const formData = new FormData();
    if (resumeSource === "saved") formData.append("saved_resume_id", selectedSavedId);
    else formData.append("resume", file);
    formData.append("job_description", jobDescription);
    formData.append("job_title", jobTitle);
    formData.append("company_name", companyName);
    formData.append("deep_optimize", deepOptimize ? "true" : "false");
    formData.append("hygiene", hygiene ? "true" : "false");
    formData.append("prune", prune ? "true" : "false");
    formData.append("format_mode", formatMode);
    formData.append("ai_model", aiModel);
    formData.append("employment_type", employmentType);
    const addSkills = (scanData?.action_plan || [])
      .filter(a => a.type === "add_skill" && selectedActions.includes(a.id))
      .map(a => a.skill);
    formData.append("approved_skills", JSON.stringify(addSkills));
    // Per-skill placement overrides chosen on the Analysis page (red skills only).
    const placements = {};
    (scanData?.action_plan || [])
      .filter(a => a.type === "add_skill" && a.bucket === "red" && selectedActions.includes(a.id))
      .forEach(a => { placements[a.skill] = skillPlacements[a.skill] || "section_only"; });
    formData.append("skill_placements", JSON.stringify(placements));
    formData.append("selected_actions", JSON.stringify(selectedActions));
    if (scanId) formData.append("scan_id", scanId);
    // Personal info for template replacement
    const pi = personalInfo;
    if (pi.full_name || pi.phone || pi.email || pi.location) {
      formData.append("personal_info_json", JSON.stringify({
        full_name: pi.full_name.trim(),
        phone: pi.phone.trim(),
        email: pi.email.trim(),
        location: pi.location.trim(),
      }));
    }
    formData.append("generate_email", generateEmail ? "true" : "false");
    formData.append("generate_cover_letter_opt", generateCoverLetter ? "true" : "false");
    if (generateCoverLetter) {
      formData.append("cover_letter_is_third_party", clIsThirdParty ? "true" : "false");
      formData.append("cover_letter_employment_type", employmentType);
    }
    if (jobUrl.trim()) formData.append("job_url", jobUrl.trim());
    if (extractedPlatform) formData.append("platform", extractedPlatform);
    if (recruiterEmail) formData.append("recruiter_email", recruiterEmail);
    if (generateEmail) {
      formData.append("include_visa", includeVisa ? "true" : "false");
      formData.append("visa_status", visaStatus);
      formData.append("match_location", matchLocation ? "true" : "false");
      formData.append("job_location", jobLocation);
      formData.append("candidate_location", candidateLocation);
      formData.append("email_length", emailLength);
      formData.append("do_not_discuss_location", doNotDiscussLocation ? "true" : "false");
      formData.append("willing_to_relocate", willingToRelocate ? "true" : "false");
      if (recruiterName.trim()) formData.append("recruiter_name", recruiterName.trim());
    }
    try {
      const submitRes = await axios.post(`${API}/optimize`, formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 });
      const jobId = submitRes.data.job_id;
      setStep(1);
      const pollInterval = 1500;
      const maxWait = 600000;
      const startTime = Date.now();
      const result = await new Promise((resolve, reject) => {
        const poll = async () => {
          if (Date.now() - startTime > maxWait) { reject(new Error("Optimization is taking too long.")); return; }
          try {
            const statusRes = await axios.get(`${API}/optimize/status/${jobId}`, { timeout: 10000 });
            const { status, step: serverStep, result: jobResult, error } = statusRes.data;
            if (serverStep) setJobStep(serverStep);
            if (serverStep === "optimizing") setStep(1);
            else if (serverStep === "scoring") setStep(2);
            else if (serverStep === "generating") setStep(3);
            else if (serverStep === "email") setStep(4);
            else if (serverStep === "cover_letter") setStep(5);
            if (status === "completed" && jobResult) resolve(jobResult);
            else if (status === "failed") reject(new Error(error || "Optimization failed."));
            else setTimeout(poll, pollInterval);
          } catch { setTimeout(poll, pollInterval); }
        };
        poll();
      });
      toast.success("Resume optimized successfully!");
      setPendingResult({ ...result, job_url: jobUrl, recruiter_email: recruiterEmail, platform: extractedPlatform, job_location: jobLocation, _rename: resumeRename });
      setShowAppliedPopup(true);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Optimization failed.";
      toast.error(msg, { duration: 8000, action: { label: "Retry", onClick: () => handleProceedToOptimize() } });
    } finally { setProcessing(false); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  const steps = [
    "Parsing your resume document",
    deepOptimize ? `Deep rewriting with ${engineLabel(aiModel)}` : `Rewriting content with ${engineLabel(aiModel)}`,
    "Calculating ATS match score",
    "Generating optimized document",
    ...(generateEmail ? ["Composing recruiter email"] : []),
    ...(generateCoverLetter ? ["Generating cover letter"] : []),
  ];
  const hasResume = resumeSource === "saved" ? !!selectedSavedId : !!file;

  const currentFileExt = useMemo(() => {
    if (resumeSource === "saved" && selectedSavedId) {
      const r = savedResumes.find(r => r.id === selectedSavedId);
      return r?.file_type || "";
    }
    if (file) return file.name.split(".").pop().toLowerCase();
    return "";
  }, [resumeSource, selectedSavedId, savedResumes, file]);

  return (
    <>
      {processing && (
        <div className="fixed inset-0 z-[55] bg-background/95 backdrop-blur-xl flex flex-col items-center justify-center px-6" data-testid="processing-overlay">
          <div className="relative mb-8">
            <div className="w-20 h-20 rounded-2xl bg-primary/5 border border-border flex items-center justify-center">
              <Sparkles size={32} className="text-foreground" />
            </div>
            <span className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-background border border-border flex items-center justify-center">
              <Loader2 size={16} className="animate-spin text-blue-600 dark:text-blue-400" />
            </span>
          </div>
          <h3 className="text-2xl lg:text-3xl font-bold tracking-tight text-foreground mb-2 text-center">{deepOptimize ? "Deep optimizing" : "Optimizing"} your resume</h3>
          <p className="text-sm text-muted-foreground mb-10 text-center max-w-sm">Rewriting your resume while preserving exact formatting, then re-scoring against the job.</p>
          <div className="w-full max-w-md flex flex-col gap-3">
            {steps.map((s, i) => (
              <div key={i} className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all duration-300 ${i === step ? "border-primary bg-primary/5" : i < step ? "border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20" : "border-border/50 bg-background"}`}>
                {i < step ? <Check size={18} className="text-emerald-600 dark:text-emerald-400" />
                  : i === step ? <Loader2 size={18} className="animate-spin text-foreground" />
                  : <span className="w-[18px] h-[18px] rounded-full border-2 border-muted-foreground/30" />}
                <span className={`text-sm ${i === step ? "font-semibold text-foreground" : i < step ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
              </div>
            ))}
          </div>
          <WaitingUX className="w-full max-w-md mt-8" progress={optimizeProgress} />
        </div>
      )}

      {/* ── Phase 2+3: Full-screen Analysis step (live scan → breakdown → consent) ── */}
      {(scanning || scanData) && !processing && !showAppliedPopup && (
        <AnalysisStep
          scanning={scanning}
          scanData={scanData}
          modelName={engineLabel(aiModel)}
          selectedActions={selectedActions}
          onToggleAction={toggleAction}
          onSelectAll={selectAllActions}
          skillPlacements={skillPlacements}
          onSetPlacement={(skill, val) => setSkillPlacements(prev => ({ ...prev, [skill]: val }))}
          resumeRename={resumeRename}
          setResumeRename={setResumeRename}
          onBack={handleBackToInput}
          onOptimize={handleProceedToOptimize}
          processing={processing}
        />
      )}

      {/* ── Applied? Popup ── */}
      {showAppliedPopup && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="applied-popup">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 text-center rz-scale-in">
            <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center mx-auto mb-4">
              <Check size={28} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold tracking-tight text-foreground mb-1">Optimization successful</h3>
            <p className="text-sm text-muted-foreground mb-5">Applied to this role? Confirm the details and we'll add it to your Job Tracker.</p>
            <div className="text-left flex flex-col gap-3 mb-5">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1 block">Company</label>
                <input className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Company name" data-testid="applied-company-input" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1 block">Role / Job Title</label>
                <input className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="e.g. Senior Backend Engineer" data-testid="applied-role-input" />
              </div>
            </div>
            <div className="flex gap-3">
              <button className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors" onClick={() => handleApplied(false)} disabled={savingApplication} data-testid="applied-no-btn">Not yet</button>
              <button className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all disabled:opacity-50" onClick={() => handleApplied(true)} disabled={savingApplication} data-testid="applied-yes-btn">
                {savingApplication ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} I Applied
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cover Letter Company Details Popup */}
      {showCoverLetterPopup && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="opt-cover-letter-popup">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg p-6 rz-scale-in">
            <div className="flex items-center gap-2 mb-1">
              <FileText size={18} className="text-foreground" />
              <h3 className="text-lg font-bold tracking-tight text-foreground">Cover Letter Details</h3>
              <button className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors" onClick={() => setShowCoverLetterPopup(false)} data-testid="opt-cl-popup-close"><X size={16} /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Enter company details to generate a tailored cover letter alongside your optimized resume.</p>
            <div className="mb-4">
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1 block">Company Name *</label>
              <input className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Enter company name..." value={companyName} onChange={e => setCompanyName(e.target.value)} autoFocus data-testid="opt-cl-company-name" />
            </div>
            <div className="mb-4">
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Is this a Third-Party / Staffing Company?</label>
              <div className="flex gap-2">
                <ChoiceChip active={!clIsThirdParty} onClick={() => setClIsThirdParty(false)} label="No — Direct Hire" testid="opt-cl-direct" />
                <ChoiceChip active={clIsThirdParty} onClick={() => setClIsThirdParty(true)} label="Yes — Staffing / Agency" testid="opt-cl-third-party" />
              </div>
            </div>
            <div className="mb-5">
              <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Employment Type</label>
              <p className="text-xs text-muted-foreground mb-2">Using your application-wide choice: <strong className="text-foreground">{EMPLOYMENT_LABELS[employmentType] || "Contract C2C"}</strong>. Change it in the options above to update everything.</p>
            </div>
            <button className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all" onClick={handleCoverLetterPopupSubmit} data-testid="opt-cl-submit">
              <Sparkles size={16} /> Optimize & Generate Cover Letter
            </button>
          </div>
        </div>
      )}

      {/* ── Phase 1: Input ── */}
      <div className="max-w-6xl mx-auto w-full">
        <div className="py-10 lg:py-14 text-center rz-fade-up" data-testid="optimize-hero">
          <div className="inline-flex items-center gap-1.5 text-[0.65rem] font-mono uppercase tracking-[0.2em] text-muted-foreground border border-border/60 rounded-full px-3 py-1 mb-5">
            <Sparkles size={12} /> Phase 1 · Set up
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-foreground leading-[1.05]">
            Tailor your resume<br /><span className="text-muted-foreground">with precision AI</span>
          </h1>
          <p className="text-base text-muted-foreground mt-4 max-w-xl mx-auto">Pick a resume, drop in the job description, choose your engine. We analyze, then optimize with your consent.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 rz-fade-up rz-d1">
          {/* Card 1 — Resume */}
          <div className="bg-card border border-border/50 rounded-xl p-6 shadow-sm flex flex-col">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-muted-foreground">Step 1</span>
            </div>
            <h3 className="text-xl font-bold tracking-tight text-foreground mb-4">Select or upload résumé</h3>
            <div className="flex gap-2 mb-4" data-testid="source-toggle">
              {savedResumes.length > 0 && (
                <ChoiceChip active={resumeSource === "saved"} onClick={() => { setResumeSource("saved"); setFile(null); }} icon={FolderOpen} label="My Résumés" testid="source-saved-btn" />
              )}
              <ChoiceChip active={resumeSource === "upload"} onClick={() => { setResumeSource("upload"); setSelectedSavedId(""); }} icon={Upload} label="Upload New" testid="source-upload-btn" />
            </div>

            {resumeSource === "saved" && savedResumes.length > 0 && (
              <div className="flex flex-col gap-2" data-testid="saved-resumes-section">
                {savedResumes.map((r) => (
                  <div key={r.id} onClick={() => setSelectedSavedId(r.id)} data-testid={`saved-resume-${r.id}`}
                    className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all ${selectedSavedId === r.id ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/40"}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0"><FileText size={16} className="text-muted-foreground" /></div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate flex items-center gap-1.5">{r.display_name}{r.is_default && <span className="text-[0.55rem] font-mono uppercase px-1.5 py-0.5 rounded bg-foreground/10 text-foreground">Default</span>}</div>
                        <div className="text-[0.72rem] text-muted-foreground truncate">{r.file_type.toUpperCase()} · {r.filename}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {selectedSavedId === r.id && <Check size={15} className="text-primary" />}
                      <button className="w-7 h-7 rounded-md flex items-center justify-center text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors" onClick={(e) => handleDeleteSaved(e, r.id)} data-testid={`delete-saved-${r.id}`} aria-label="Remove"><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {resumeSource === "upload" && (
              <>
                <p className="text-xs text-muted-foreground mb-3">Drop your DOC or DOCX. We preserve every font, color, and table.</p>
                <div {...getRootProps()} data-testid="resume-dropzone"
                  className={`border-2 border-dashed rounded-xl px-6 py-10 text-center cursor-pointer transition-all ${isDragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 hover:bg-muted/30"}`}>
                  <input {...getInputProps()} data-testid="resume-file-input" />
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3"><Upload size={20} className="text-muted-foreground" /></div>
                  <p className="text-sm text-foreground">Drag &amp; drop or <strong className="font-semibold">click to browse</strong></p>
                  <p className="text-xs text-muted-foreground mt-1">.doc and .docx only</p>
                </div>
                {file && (
                  <>
                    <div className="flex items-center gap-3 mt-3 px-4 py-3 rounded-lg border border-border bg-muted/20" data-testid="file-selected-info">
                      <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center shrink-0"><FileText size={16} className="text-emerald-600 dark:text-emerald-400" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-foreground truncate" data-testid="file-name">{file.name}</div>
                        <div className="text-[0.72rem] text-muted-foreground">{formatSize(file.size)}</div>
                      </div>
                      <button className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-muted transition-colors" onClick={(e) => { e.stopPropagation(); setFile(null); }} data-testid="file-remove-btn" aria-label="Remove file"><X size={15} /></button>
                    </div>
                    {!showSaveOption ? (
                      <button className="mt-3 self-start inline-flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline" onClick={() => setShowSaveOption(true)} data-testid="save-to-library-btn"><Save size={14} /> Save to My Résumés</button>
                    ) : (
                      <div className="mt-3 flex flex-col gap-2">
                        <input className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Display name (optional)" value={saveDisplayName} onChange={(e) => setSaveDisplayName(e.target.value)} data-testid="save-display-name" />
                        <div className="flex gap-2">
                          <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all" onClick={handleSaveResume} data-testid="save-confirm-btn"><Check size={14} /> Save</button>
                          <button className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors" onClick={() => setShowSaveOption(false)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Card 2 — Job description + options */}
          <div className="bg-card border border-border/50 rounded-xl p-6 shadow-sm flex flex-col gap-4">
            <div>
              <span className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-muted-foreground">Step 2</span>
              <h3 className="text-xl font-bold tracking-tight text-foreground mt-1">Job description</h3>
            </div>

            <div className="flex gap-2" data-testid="jd-mode-toggle">
              <ChoiceChip active={jdInputMode === "text"} onClick={() => setJdInputMode("text")} icon={FileText} label="Paste Text" testid="jd-mode-text" />
              <ChoiceChip active={jdInputMode === "link"} onClick={() => setJdInputMode("link")} icon={Link} label="Job Link" testid="jd-mode-link" />
            </div>

            {jdInputMode === "link" ? (
              <div data-testid="jd-link-section" className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <input className="flex-1 px-3 py-2.5 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="https://linkedin.com/jobs/... or any job page URL" value={jobUrl} onChange={(e) => setJobUrl(e.target.value)} data-testid="jd-url-input" />
                  <button className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all disabled:opacity-50 shrink-0" onClick={handleExtractJD} disabled={extracting || !jobUrl.trim()} data-testid="jd-extract-btn">
                    {extracting ? <><Loader2 size={14} className="animate-spin" /> Extracting…</> : <><Sparkles size={14} /> Extract</>}
                  </button>
                </div>
                <p className="text-[0.7rem] text-muted-foreground">Works best with Greenhouse, Lever, company careers, Dice, ZipRecruiter. Indeed &amp; Monster may need manual paste.</p>
              </div>
            ) : (
              <textarea className="w-full min-h-[200px] p-4 text-sm rounded-lg border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 leading-relaxed" placeholder="Paste the job description here..." value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} data-testid="job-description-input" />
            )}

            <div className="grid grid-cols-2 gap-2">
              <input className="px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Job Title (optional)" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} data-testid="job-title-input" />
              <input className="px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Company Name (optional)" value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="company-name-input" />
            </div>

            {/* Main toggles */}
            <div className="border-t border-border/50 pt-2 divide-y divide-border/40" data-testid="main-actions-section">
              <ToggleRow icon={Mail} label="Generate Recruiter Email" on={generateEmail} onClick={() => setGenerateEmail(!generateEmail)} testid="generate-email-toggle" />
              <ToggleRow icon={MapPin} label="Match Job Location" on={matchLocation} onClick={handleMatchLocationToggle} testid="location-toggle-upload"
                badge={localDetected ? <span className="text-[0.55rem] font-mono uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400" data-testid="local-detected-badge">Local</span> : null} />
            </div>

            {/* Advanced options */}
            <details className="border border-border/50 rounded-lg" data-testid="advanced-options-panel">
              <summary className="cursor-pointer select-none list-none px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-foreground" data-testid="advanced-options-toggle">
                <ChevronDown size={14} className="text-muted-foreground" /> Advanced options
              </summary>
              <div className="px-4 pb-3 divide-y divide-border/40">
                <ToggleRow icon={Zap} label="Deep Optimize" desc="Extensively rewrites for maximum alignment" on={deepOptimize} onClick={() => setDeepOptimize(!deepOptimize)} testid="deep-optimize-toggle" />
                <ToggleRow icon={Sparkles} label="Resume Hygiene" desc="Cleans your skills table: fixes duplicates, garbled entries & obvious mis-categorization" on={hygiene} onClick={() => setHygiene(!hygiene)} testid="hygiene-toggle" />
                <ToggleRow icon={Scissors} label="Trim duplicate bullets" desc="Removes repeated/near-duplicate experience bullets so your resume stays tight (off by default)" on={prune} onClick={() => setPrune(!prune)} testid="prune-toggle" />
                <ToggleRow icon={FileText} label="Generate Cover Letter" desc="Tailored cover letter in the same request" on={generateCoverLetter} onClick={() => setGenerateCoverLetter(!generateCoverLetter)} testid="generate-cover-letter-toggle" />
                {generateEmail && (
                  <div className="py-3 flex flex-col gap-3">
                    <input className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Recruiter name (optional — leave blank for 'Hi,')" value={recruiterName} onChange={(e) => setRecruiterName(e.target.value)} data-testid="recruiter-name-input" />
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-foreground">Email Length</span>
                      <div className="flex gap-1.5">
                        <ChoiceChip active={emailLength === "short"} onClick={() => setEmailLength("short")} label="Short" testid="email-length-short-opt" />
                        <ChoiceChip active={emailLength === "long"} onClick={() => setEmailLength("long")} label="Long" testid="email-length-long-opt" />
                      </div>
                    </div>
                    <ToggleRow label="Do Not Discuss Current Location" on={doNotDiscussLocation} onClick={handleDoNotDiscussToggle} testid="no-location-toggle-opt" />
                    <ToggleRow label="Willing to Relocate" on={willingToRelocate} onClick={handleRelocateToggle} testid="relocate-toggle-opt" />
                    <ToggleRow label="Include Visa Status" on={includeVisa} onClick={() => setIncludeVisa(!includeVisa)} testid="visa-toggle-upload" />
                    {includeVisa && (
                      <div className="relative" data-testid="visa-status-wrapper">
                        <select className="w-full appearance-none px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" value={visaStatus} onChange={(e) => setVisaStatus(e.target.value)} data-testid="visa-status-upload">
                          {VISA_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      </div>
                    )}
                    <div>
                      <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1 block">Your Current Location</span>
                      <input className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="e.g. Austin, TX" value={candidateLocation} onChange={(e) => setCandidateLocation(e.target.value)} onBlur={(e) => syncLocationToProfile(e.target.value)} data-testid="candidate-location-upload" />
                    </div>
                  </div>
                )}
                <div className="pt-2.5 text-[0.7rem] text-muted-foreground flex items-center gap-1.5" data-testid="prefs-saved-note"><Check size={11} /> Settings auto-saved</div>
              </div>
            </details>

            {/* Resume mode */}
            {hasResume && currentFileExt === "docx" && (
              <div data-testid="format-mode-section">
                <div className="flex items-center gap-2 mb-2"><ShieldCheck size={14} className="text-muted-foreground" /><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Résumé Mode</span></div>
                <div className="flex gap-2">
                  <ChoiceChip active={formatMode === "clone_fill"} onClick={() => setFormatMode("clone_fill")} icon={Shield} label="My Template" badge="Best" badgeClass="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400" testid="format-mode-clone-fill" />
                  <ChoiceChip active={formatMode === "preserve"} onClick={() => setFormatMode("preserve")} icon={FileText} label="Edit Original" testid="format-mode-preserve" />
                  <ChoiceChip active={formatMode === "third_party"} onClick={() => setFormatMode("third_party")} icon={RefreshCw} label="Third-Party Fix" testid="format-mode-third-party" />
                </div>
              </div>
            )}

            {/* AI engine */}
            <div data-testid="ai-model-section">
              <div className="flex items-center gap-2 mb-2"><Cpu size={14} className="text-muted-foreground" /><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">AI Engine</span></div>
              <div className="flex gap-2 flex-wrap">
                {ENGINE_KEYS_FULL.map((m) => (
                  <ChoiceChip key={m} active={aiModel === m} onClick={() => selectModel(m)}
                    icon={m === "glm-5.2" ? Sparkles : m === "deepseek-v4-pro" ? Rocket : Zap}
                    label={ENGINES[m].label} badge={ENGINES[m].badge} badgeClass={ENGINES[m].badgeClass}
                    locked={isModelLocked(m)}
                    testid={`ai-model-${m === "glm-5.2" ? "glm" : m === "deepseek-v4-pro" ? "deepseek" : "gpt"}`} />
                ))}
              </div>
            </div>

            <div data-testid="employment-type-section">
              <div className="flex items-center gap-2 mb-2"><Briefcase size={14} className="text-muted-foreground" /><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Employment Type</span></div>
              <div className="max-w-xs">
                <EmploymentSelect value={employmentType} onChange={(v) => setEmploymentType(v)} testid="opt-employment-select" />
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">Applies to this optimization, cover letter and recruiter email.</p>
            </div>

            <button className="mt-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50" onClick={handleOptimize} disabled={!hasResume || !jobDescription.trim() || processing || scanning} data-testid="optimize-btn">
              {scanning ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {scanning ? "Scanning…" : processing ? "Optimizing…" : "Analyze & Optimize"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}







/* ── Main OptimizePage Export ── */
export default function OptimizePage() {
  const [view, setView] = useState("upload");
  const [result, setResult] = useState(null);
  const [sessionKey, setSessionKey] = useState(0);

  const handleResult = (data) => { setResult(data); setView("results"); };
  const handleBack = () => { setResult(null); setSessionKey((k) => k + 1); setView("upload"); };

  return (
    <div className="optimize-flow" data-testid="optimize-page">
      {view === "upload" && <UploadSection key={sessionKey} onResult={handleResult} />}
      {view === "results" && result && (
        <ResultsStep
          result={result}
          onBack={handleBack}
          onUpdateResult={(data) => setResult((prev) => ({ ...prev, optimized_text: data.optimized_text, ats_score: data.ats_score, cost_info: { ...prev.cost_info, after_breakdown: data.after_breakdown || prev.cost_info?.after_breakdown } }))}
        />
      )}
    </div>
  );
}
